/**
 * Gate Decision Service
 *
 * API client for persisting and retrieving user remediation decisions.
 * Decisions are keyed on (account_id, application_name, gate_job_id, fingerprint)
 * in SQL Server.  gate_job_id scopes decisions to a specific compile run so that
 * decisions from an old pflt job cannot mark new-run issues as checked.
 *
 * Fingerprint formula — must match gateIssueNormalizer.stableHash:
 *   stableHash(`${gate_type}:${language}:${file}:${code}:${line}`)
 *
 * Endpoints:
 *   POST   /gate-decisions/upsert
 *   GET    /gate-decisions/{account_id}/{application_name}?gate_type=&gate_job_id=
 *   DELETE /gate-decisions/{decision_id}
 */

import { getAuthHeaders } from '../nodeExecutors/authHelper'
import { getAPIBaseUrl } from '../../environment'

// ─────────────────────────────────────────────────────────────────────────────
// upsertDecision
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Create or update a gate decision.
 *
 * Upserts on (account_id, application_name, gate_job_id, fingerprint) when
 * gate_job_id is provided, so the same fingerprint can have independent
 * decisions across different compile runs.
 *
 * @param {object} payload
 * @param {string}  payload.account_id
 * @param {string}  payload.application_name
 * @param {string}  payload.fingerprint        - from Issue.fingerprint
 * @param {string}  payload.gate_type          - 'structural' | 'compile'
 * @param {string}  payload.decision_type      - 'MARK_TODO_STUB' | 'SUPPRESS' | 'CUSTOM_EDIT'
 * @param {string}  [payload.gate_job_id]      - pflt_... / java_validation_... job id
 * @param {string}  [payload.target_file]      - bare Java filename
 * @param {string}  [payload.target_symbol]    - method/class name for display
 * @param {string}  [payload.patch_json]       - JSON string: patch payload
 * @param {string}  [payload.language='java']
 * @param {string}  [payload.application_id]   - optional FK to Applications table
 * @param {string}  [payload.user_id]
 * @returns {Promise<{decision_id: string, status: string}>}
 */
export async function upsertDecision(payload) {
  const res = await fetch(`${getAPIBaseUrl()}/gate-decisions/upsert`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      language: 'java',
      ...payload,
    }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.detail || `upsertDecision: ${res.status}`)
  }
  return res.json()
}

// ─────────────────────────────────────────────────────────────────────────────
// fetchDecisions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch active decisions for an account + application, scoped to a gate job.
 *
 * When gate_job_id is supplied the API returns only decisions for that job,
 * preventing cross-job decision bleed (e.g. old pflt decisions marking new
 * run issues as checked).  Omit gate_job_id on the worker/applier path.
 *
 * Returns [] on error (non-fatal — UI still works without persisted decisions).
 *
 * @param {string} account_id
 * @param {string} application_name
 * @param {string} [gate_type]    - optional filter: 'structural' | 'compile'
 * @param {string} [gate_job_id]  - scope to this job; omit for all-decisions path
 * @param {string} [language]     - optional filter: default 'java'
 * @returns {Promise<Array>}
 */
export async function fetchDecisions(account_id, application_name, gate_type, gate_job_id, language) {
  const params = new URLSearchParams()
  if (gate_type)   params.set('gate_type', gate_type)
  if (gate_job_id) params.set('gate_job_id', gate_job_id)
  if (language)    params.set('language', language)

  const qs = params.toString() ? `?${params}` : ''
  const url = `${getAPIBaseUrl()}/gate-decisions/${encodeURIComponent(account_id)}/${encodeURIComponent(application_name)}${qs}`

  try {
    const res = await fetch(url, { headers: getAuthHeaders() })
    if (!res.ok) return []
    return await res.json()
  } catch (_) {
    return []
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// deleteDecision
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Soft-delete a decision (sets is_active = 0 in DB).
 *
 * @param {string} decision_id
 * @returns {Promise<void>}
 */
export async function deleteDecision(decision_id) {
  const res = await fetch(`${getAPIBaseUrl()}/gate-decisions/${decision_id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.detail || `deleteDecision: ${res.status}`)
  }
}
